import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { EvmChainId, tokenPath } from '../utils/chains';
import Header from './Header';
import Footer from './Footer';
import HolderDistributionChart from './HolderDistributionChart';
import { apiFetch, getApiHost } from '../utils/api';

const API_HOST = getApiHost();

interface HistoryEntry {
  timestamp: string;
  overallSafetyLevel: string;
  criticalRisks: string[];
  warnings: string[];
  tokenName?: string;
  tokenSymbol?: string;
  rugcheckScore?: number;
  totalHolders?: number;
  topHolderOwnership?: number;
  topTenOwnership?: number;
  ruggerHoldingsPct?: number;
  topHolderRiskLevel?: string;
  topTenHoldersRiskLevel?: string;
  topNetworkOwnership?: number;
  topNetworkWalletCount?: number;
  topNetworkBundlerName?: string;
  isMintable?: boolean;
  isFreezable?: boolean;
  exchangeDistributionTag?: string;
  exchangeDistributionWarning?: string;
  holderDistribution?: Array<{
    name: string;
    value: number;
    walletCount: number;
    holdingsPercentage: number;
  }>;
  trigger?: string;
}

const SAFETY_COLORS: Record<string, string> = {
  OK: 'text-green-400',
  RISKY: 'text-yellow-400',
  DANGEROUS: 'text-red-400',
};

const SAFETY_BG: Record<string, string> = {
  OK: 'border-green-500/30 bg-green-500/5',
  RISKY: 'border-yellow-500/30 bg-yellow-500/5',
  DANGEROUS: 'border-red-500/30 bg-red-500/5',
};

function formatDate(ts: string): string {
  return new Date(ts).toLocaleString();
}

function formatTimeAgo(ts: string): string {
  const diffMs = Date.now() - new Date(ts).getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const diffHrs = Math.floor(diffMin / 60);
  const diffDays = Math.floor(diffHrs / 24);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHrs < 24) return `${diffHrs}h ago`;
  return `${diffDays}d ago`;
}

function formatPct(n?: number): string {
  if (n == null) return '—';
  return `${n.toFixed(1)}%`;
}

const TokenSafetyHistoryPage: React.FC<{ chain?: EvmChainId }> = ({ chain }) => {
  const { mint } = useParams<{ mint: string }>();
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [gated, setGated] = useState(false);

  useEffect(() => {
    if (!mint) return;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const headers: Record<string, string> = {};
        const jwt = localStorage.getItem('deepnets_token');
        if (jwt) headers['Authorization'] = `Bearer ${jwt}`;
        const res = await apiFetch(`${API_HOST}/api/token-safety-history/${mint}${chain ? `?chain=${chain}` : ''}`, { headers });
        if (res.status === 401 || res.status === 403) {
          setGated(true);
          return;
        }
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        setHistory(data.history || []);
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        setLoading(false);
      }
    })();
  }, [mint, chain]);

  const tokenName = history[0]?.tokenName || mint?.slice(0, 8) || '';
  const tokenSymbol = history[0]?.tokenSymbol || '';

  return (
    <div className="min-h-screen bg-crypto-dark text-white">
      <Header />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-6">
        <div className="mb-6">
          <Link to={mint ? tokenPath(mint, chain) : '/'} className="text-sm text-gray-400 hover:text-crypto-accent transition-colors">
            <i className="fas fa-arrow-left mr-1" /> Back to {tokenName} {tokenSymbol && `($${tokenSymbol})`}
          </Link>
        </div>

        <h1 className="text-xl font-bold mb-1">Safety Analysis History</h1>
        <p className="text-sm text-gray-400 mb-6">
          {tokenName} {tokenSymbol && <span className="text-gray-500">({tokenSymbol})</span>}
          <span className="ml-2 text-gray-600 text-xs font-mono">{mint}</span>
        </p>

        {loading && (
          <div className="space-y-4">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="bg-crypto-gray border border-gray-700 rounded-xl p-4 animate-pulse">
                <div className="h-5 w-32 bg-gray-700 rounded mb-3" />
                <div className="h-4 w-48 bg-gray-700 rounded mb-2" />
                <div className="h-4 w-64 bg-gray-700 rounded" />
              </div>
            ))}
          </div>
        )}

        {gated && (
          <div className="text-center py-16">
            <i className="fas fa-lock text-gray-600 text-4xl mb-4 block" />
            <p className="text-gray-400 mb-2">Watcher tier required to view safety history</p>
            <Link to="/tiers" className="text-crypto-accent hover:underline text-sm">View tiers</Link>
          </div>
        )}

        {error && !loading && (
          <div className="text-center py-16">
            <i className="fas fa-exclamation-triangle text-red-400 text-4xl mb-4 block" />
            <p className="text-gray-400">{error}</p>
          </div>
        )}

        {!loading && !error && !gated && history.length === 0 && (
          <div className="text-center py-16 text-gray-500">
            <i className="fas fa-clipboard-list text-4xl mb-4 block" />
            <p>No safety analysis history found for this token.</p>
          </div>
        )}

        {!loading && !gated && history.length > 0 && (
          <div className="space-y-4">
            {history.map((entry, idx) => (
              <div key={idx} className={`border rounded-xl p-4 ${SAFETY_BG[entry.overallSafetyLevel] || 'border-gray-700 bg-crypto-gray'}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <span className={`text-lg font-bold ${SAFETY_COLORS[entry.overallSafetyLevel] || 'text-gray-400'}`}>
                      {entry.overallSafetyLevel}
                    </span>
                  </div>
                  <div className="text-right">
                    <div className="text-sm text-gray-400">{formatTimeAgo(entry.timestamp)}</div>
                    <div className="text-xs text-gray-600">{formatDate(entry.timestamp)}</div>
                  </div>
                </div>

                {/* Risks */}
                {entry.criticalRisks.length > 0 && (
                  <div className="mb-2">
                    {entry.criticalRisks.map((risk, ri) => (
                      <div key={ri} className="text-sm text-red-400 flex items-start gap-1.5">
                        <i className="fas fa-exclamation-circle mt-0.5 text-xs" /> {risk}
                      </div>
                    ))}
                  </div>
                )}
                {entry.warnings.length > 0 && (
                  <div className="mb-2">
                    {entry.warnings.map((w, wi) => (
                      <div key={wi} className="text-sm text-yellow-400/80 flex items-start gap-1.5">
                        <i className="fas fa-exclamation-triangle mt-0.5 text-xs" /> {w}
                      </div>
                    ))}
                  </div>
                )}

                {/* Key metrics */}
                <div className="flex flex-wrap gap-x-6 gap-y-1 mt-3 text-xs text-gray-400">
                  {entry.totalHolders != null && <span>Holders: <span className="text-gray-300">{entry.totalHolders}</span></span>}
                  {entry.topHolderOwnership != null && <span>Top holder: <span className="text-gray-300">{formatPct(entry.topHolderOwnership)}</span></span>}
                  {entry.topTenOwnership != null && <span>Top 10: <span className="text-gray-300">{formatPct(entry.topTenOwnership)}</span></span>}
                  {entry.ruggerHoldingsPct != null && (
                    <span>Ruggers: <span className={entry.ruggerHoldingsPct >= 10 ? 'text-red-400' : entry.ruggerHoldingsPct >= 5 ? 'text-yellow-400' : 'text-gray-300'}>{formatPct(entry.ruggerHoldingsPct)}</span></span>
                  )}
                  {entry.topNetworkOwnership != null && entry.topNetworkOwnership > 0 && (
                    <span>
                      Network: <span className="text-gray-300">
                        {formatPct(entry.topNetworkOwnership)} ({entry.topNetworkWalletCount} wallets)
                        {entry.topNetworkBundlerName && (
                          <> — <span className="text-gray-400 italic">{entry.topNetworkBundlerName}</span></>
                        )}
                      </span>
                    </span>
                  )}
                  {entry.exchangeDistributionTag && (
                    <span>Exchange dist: <span className="text-yellow-400/80">{entry.exchangeDistributionTag.replace(/_/g, ' ')}</span></span>
                  )}
                  {entry.isMintable && <span className="text-red-400">Mintable</span>}
                  {entry.isFreezable && <span className="text-red-400">Freezable</span>}
                </div>

                {/* Exchange distribution chart */}
                {entry.holderDistribution && entry.holderDistribution.length > 0 && (
                  <div className="mt-4">
                    <HolderDistributionChart
                      holderDistribution={entry.holderDistribution}
                      exchangeDistributionWarning={entry.exchangeDistributionWarning}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default TokenSafetyHistoryPage;
